import React, { useCallback, useEffect, useMemo, useState } from "react";

import PanelPermissionError from "@components/property-panel/ui/PanelPermissionError";
import Loading from "@components/loader/Loading";
import PanelBody from "@components/property-panel/ui/PanelBody";
import VersionedComponent from "@components/VersionedComponent";
import { Box, Button, Divider, Grid, IconButton, Tooltip, Typography } from "@mzc-pdc/ui";
import { IconForward, IconLeave } from "@mzc-cloudplex/icons";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import theme from "@styles/theme";
import EditName from "@components_v2/edit-name/edit-name";
import styled from "@emotion/styled";
import EditSubmitButtonGroup from "@components_v2/edit-submit-button-group/edit-submit-button-group";
import CollectionThumbnail from "@features/thumbnail/collection-thumbnail";
import { VERSION_CM_9766_COLLECTIONS } from "@cores/version";
import { TabPanel, Tabs } from "@components_v2/tabs/tabs";
import PanelDetailsView from "@components/view/panel-details-view/panel-details-view";
import * as PropertyPanelActions from "@modules/propertyPanel";
import {
    getData,
    updateCollection,
    updatePropertyPanelData,
    updateCustomContent,
    updateCustomContentActions,
    updatePromptPreset,
} from "@modules/propertyPanel";
import { FOLDER_VIEW_DOMAINS, PROPERTY_PANEL_TYPE, LIMIT_CHAR } from "@constants";
import PanelAssetsView from "@components/view/panel-assets-view/panel-assets-view";
import { setNotification } from "@modules/notification";
import { updateCollectionData } from "@modules/collections";
import { getPropertyPanelCollectionData } from "@features/property-panel/property-panel-collection-data";
import {
    getPropertyPanelCustomContentMetadata,
    getPropertyPanelCustomContentOverview,
} from "@features/property-panel/property-panel-custom-content-data";
import PanelOverview from "@components/view/panel-overview/panel-overview";
import PanelMetadata from "@components/view/panel-metadata/panel-metadata";
import CustomContentThumbnail from "@features/thumbnail/custom-content-thumbnail";
import menuService from "@services/menuService";
import { useHistory } from "react-router";
import useCustomContent from "@hooks/useCustomContent";
import useCheckPermission from "@hooks/useCheckPermission";
import { getPropertyPanelAdminAuditLogsOverview } from "@features/property-panel/property-panel-admin-audit-logs-data";
import ReactJson from "@microlink/react-json-view";
import { getPropertyPanelPromptPresetsMetadata } from "@features/property-panel/property-panel-prompt-presets";
import PanelExpiredError from "@components/property-panel/ui/PanelExpiredError";
import PanelCommonError from "@components/property-panel/ui/PanelCommonError";
import { updatePromptPresetData } from "@modules/prompt-presets";
import validateInput from "@cores/validateInput";

export const PanelHeader = styled(Grid)(({ theme }) => ({
    display: `flex`,
    alignItems: `center`,
    justifyContent: `space-between`,
    padding: `10px 8px 10px 12px`,
    borderBottom: `1px solid ${theme.palette.divider}`,
}));

const TAB_TYPE = {
    DETAILS: "Details",
    ASSETS: "Assets",
    OVERVIEW: "Overview",
    METADATA: "Metadata",
    PAYLOAD: "Payload",
};
const PropertyPanelV2 = ({ onClickClose, handleShareRequestModal }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const { spaceId, projectId } = menuService.parse(location.pathname);
    const { createDefaultValueSchema } = useCustomContent();
    const COLLECTIONS_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Assets" });
    const CUSTOM_CONTENTS_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "CustomContents" });

    const [editName, setEditName] = useState("");
    const [validationErrors, setValidationErrors] = useState({});
    const [editNameActive, setEditNameActive] = useState(false);
    const [actionPending, setActionPending] = useState({});
    const [activeTab, setActiveTab] = useState();

    const propertyPanel = useSelector(({ propertyPanel }) => propertyPanel);
    const myProjectPermissions = useSelector(({ roleManagementV2 }) => roleManagementV2?.projectPermissions?.data);

    const onHandleChangeTab = (tab) => {
        setActiveTab(tab);
    };

    const moveToDetails = useCallback(() => {
        if (!propertyPanel.data) return;
        if (propertyPanel.type === PROPERTY_PANEL_TYPE.COLLECTION)
            return history.push(menuService.paths.assetsCollectionDetail(spaceId, projectId, propertyPanel.targetId));
        if (propertyPanel.type === PROPERTY_PANEL_TYPE.CUSTOM_CONTENT)
            return history.push(
                menuService.paths.contentsCustomContentDetail(
                    spaceId,
                    projectId,
                    propertyPanel.data?.contentSchema?.id,
                    propertyPanel.targetId,
                ),
            );
    }, [propertyPanel.data]);

    const onHandleSubmitUpdateContent = useCallback(
        async (key, value) => {
            try {
                if (key !== "name") setActionPending({ ...actionPending, [key]: true });

                await dispatch(updateCustomContent({ contentId: propertyPanel.targetId, params: { [key]: value } }));
            } catch (error) {
                console.log(error);
                dispatch(
                    setNotification({
                        type: "error",
                        contents: t(
                            `common::msg::An error occured while changing collection {{type}}`,
                            `common::msg::An error occured while changing collection ${key}`,
                            { type: t(`common::label::${key}`, key) },
                        ),
                    }),
                );
            } finally {
                if (key !== "name") setActionPending({ ...actionPending, [key]: false });
            }
        },
        [propertyPanel.targetId],
    );

    const onHandleSubmitUpdateContentAction = useCallback(
        async (key, value) => {
            try {
                setActionPending({ ...actionPending, [key]: true });

                await dispatch(
                    updateCustomContentActions({
                        contentId: propertyPanel.targetId,
                        params: { key, value },
                    }),
                );
            } catch (error) {
                console.log(error);
                dispatch(
                    setNotification({
                        type: "error",
                        contents: t(
                            `common::msg::An error occured while changing collection {{type}}`,
                            `common::msg::An error occured while changing collection ${key}`,
                            { type: t(`common::label::${key}`, key) },
                        ),
                    }),
                );
            } finally {
                setActionPending({ ...actionPending, [key]: false });
            }
        },
        [propertyPanel.data],
    );

    const onHandleSubmitUpdatePreset = useCallback(
        async (key, value) => {
            try {
                setActionPending({ ...actionPending, [key]: true });
                const newData = await dispatch(
                    updatePromptPreset({
                        projectId,
                        presetId: propertyPanel.targetId,
                        data: { [key]: value },
                    }),
                );
                dispatch(updatePromptPresetData(newData));
            } catch (error) {
                console.log(error);
            } finally {
                setActionPending({ ...actionPending, [key]: false });
            }
        },
        [propertyPanel.targetId, dispatch, projectId],
    );

    const isEditNameEditable = useMemo(() => {
        if (!propertyPanel.data) return true;

        switch (propertyPanel.type) {
            case PROPERTY_PANEL_TYPE.COLLECTION:
                return COLLECTIONS_PERMISSIONS.EDIT;
            case PROPERTY_PANEL_TYPE.CUSTOM_CONTENT:
                return CUSTOM_CONTENTS_PERMISSIONS.EDIT;
            case PROPERTY_PANEL_TYPE.PROMPT_PRESETS:
                return !propertyPanel.data.isSystemManaged && projectId === (propertyPanel.data.project?.id ?? null); //Note: 프리셋은 어드민 페이지에서만 보이기 때문에 권한은 항상 존재.
            default:
                return false;
        }
    }, [propertyPanel.data, projectId, propertyPanel.type, COLLECTIONS_PERMISSIONS, CUSTOM_CONTENTS_PERMISSIONS]);

    const tabOptions = useMemo(() => {
        if (!propertyPanel.data) return [];
        switch (propertyPanel.type) {
            case PROPERTY_PANEL_TYPE.COLLECTION:
                return [
                    {
                        label: TAB_TYPE.DETAILS,
                        value: TAB_TYPE.DETAILS,
                        data: getPropertyPanelCollectionData({
                            data: propertyPanel.data,
                            isEditButtonDisabled: !COLLECTIONS_PERMISSIONS.EDIT,
                        }),
                    },
                    {
                        label: TAB_TYPE.ASSETS,
                        value: TAB_TYPE.ASSETS,
                        data: {
                            isEditButtonDisabled: !COLLECTIONS_PERMISSIONS.EDIT,
                        },
                    },
                ];
            case PROPERTY_PANEL_TYPE.CUSTOM_CONTENT:
                return [
                    {
                        label: TAB_TYPE.OVERVIEW,
                        value: TAB_TYPE.OVERVIEW,
                        data: getPropertyPanelCustomContentOverview({
                            data: propertyPanel.data,
                            onHandleSubmitUpdateContent,
                            isEditButtonDisabled: !CUSTOM_CONTENTS_PERMISSIONS.EDIT,
                        }),
                    },
                    {
                        label: TAB_TYPE.METADATA,
                        value: TAB_TYPE.METADATA,
                        data: getPropertyPanelCustomContentMetadata({
                            data: propertyPanel.data,
                            createDefaultValueSchema,
                            onHandleSubmitUpdateContent,
                            onHandleSubmitUpdateContentAction,
                            isEditButtonDisabled: !CUSTOM_CONTENTS_PERMISSIONS.EDIT,
                        }),
                    },
                ];
            case PROPERTY_PANEL_TYPE.ADMIN_AUDIT_LOGS:
                return [
                    {
                        label: TAB_TYPE.OVERVIEW,
                        value: TAB_TYPE.OVERVIEW,
                        data: getPropertyPanelAdminAuditLogsOverview(propertyPanel.data),
                    },
                    {
                        label: TAB_TYPE.PAYLOAD,
                        value: TAB_TYPE.PAYLOAD,
                        data: null,
                    },
                ];
            case PROPERTY_PANEL_TYPE.PROMPT_PRESETS:
                return [
                    {
                        label: TAB_TYPE.METADATA,
                        value: TAB_TYPE.METADATA,
                        data: getPropertyPanelPromptPresetsMetadata({
                            data: propertyPanel.data,
                            onHandleSubmitUpdatePreset,
                            isEditButtonDisabled:
                                propertyPanel.data.isSystemManaged ||
                                projectId !== (propertyPanel.data.project?.id ?? null), //Note: 프리셋은 어드민 페이지에서만 보이기 때문에 권한은 항상 존재.
                        }),
                    },
                ];
            default:
                return [];
        }
    }, [
        propertyPanel.type,
        propertyPanel.data,
        projectId,
        onHandleSubmitUpdatePreset,
        CUSTOM_CONTENTS_PERMISSIONS,
        COLLECTIONS_PERMISSIONS,
        createDefaultValueSchema,
        onHandleSubmitUpdateContent,
        onHandleSubmitUpdateContentAction,
    ]);

    const thumbnail = useMemo(() => {
        if (!propertyPanel.data) return null;

        switch (propertyPanel.type) {
            case PROPERTY_PANEL_TYPE.COLLECTION:
                return (
                    <CollectionThumbnail variant={"panel"} collection={propertyPanel.data} overrideImageCrop={false} />
                );
            case PROPERTY_PANEL_TYPE.CUSTOM_CONTENT:
                return <CustomContentThumbnail variant={"panel"} content={propertyPanel.data} />;
        }
    }, [propertyPanel.data]);

    const nameType = useMemo(() => {
        if (!propertyPanel.data) return "";

        switch (propertyPanel.type) {
            case PROPERTY_PANEL_TYPE.COLLECTION:
                return `collection`;
            case PROPERTY_PANEL_TYPE.CUSTOM_CONTENT:
                return `content`;
            case PROPERTY_PANEL_TYPE.PROMPT_PRESETS:
                return `preset`;
        }
    }, [propertyPanel.data]);

    const folderViewDomain = useMemo(() => {
        switch (propertyPanel.type) {
            case PROPERTY_PANEL_TYPE.ASSET:
                return FOLDER_VIEW_DOMAINS.ASSETS;
            case PROPERTY_PANEL_TYPE.ARCHIVED_ASSET:
                return FOLDER_VIEW_DOMAINS.ARCHIVED_ASSETS;
            default:
                return undefined;
        }
    });

    const onHandleChangeEditName = (e) => {
        e.stopPropagation();
        setEditName(e.target.value);
        setValidationErrors({ name: validateInput(e.target.value, { required: true, maxLength: LIMIT_CHAR.name }) });
    };

    const onHandleClickEditName = async () => {
        try {
            setActionPending({ ...actionPending, editName: true });

            if (propertyPanel.type === PROPERTY_PANEL_TYPE.COLLECTION) {
                await dispatch(updateCollection({ id: propertyPanel.targetId, params: { name: editName } }));
                dispatch(updateCollectionData({ id: propertyPanel.targetId, type: "name", value: editName }));
            } else if (propertyPanel.type === PROPERTY_PANEL_TYPE.CUSTOM_CONTENT) {
                await onHandleSubmitUpdateContent("name", editName);
            } else if (propertyPanel.type === PROPERTY_PANEL_TYPE.PROMPT_PRESETS) {
                await onHandleSubmitUpdatePreset("name", editName);
            }

            setEditNameActive(false);
        } catch (error) {
            dispatch(
                setNotification({
                    type: "error",
                    contents: t(`common::msg::An error occured while changing ${nameType} name`),
                }),
            );
        } finally {
            setActionPending({ ...actionPending, editName: undefined });
        }
    };

    const getTabPanelContents = (tabType) => {
        switch (tabType) {
            case TAB_TYPE.DETAILS:
                return (
                    <PanelDetailsView
                        id={propertyPanel.targetId}
                        data={tabOptions.find((el) => el.label === TAB_TYPE.DETAILS)?.data}
                        actions={PropertyPanelActions}
                        afterMutation={(params) => dispatch(updatePropertyPanelData(params))}
                    />
                );
            case TAB_TYPE.ASSETS:
                return (
                    <PanelAssetsView
                        isEditButtonDisabled={
                            tabOptions.find((el) => el.label === TAB_TYPE.ASSETS)?.data.isEditButtonDisabled
                        }
                    />
                );
            case TAB_TYPE.OVERVIEW:
                return (
                    <PanelOverview
                        id={propertyPanel.data?.id}
                        data={tabOptions.find((el) => el.label === TAB_TYPE.OVERVIEW)?.data}
                        afterMutation={(params) => {}}
                        domain={folderViewDomain}
                    />
                );
            case TAB_TYPE.METADATA:
                return (
                    <PanelMetadata
                        id={propertyPanel.data?.id}
                        data={tabOptions.find((el) => el.label === TAB_TYPE.METADATA)?.data}
                        afterMutation={(params) => {}}
                    />
                );
            case TAB_TYPE.PAYLOAD: {
                const src =
                    propertyPanel.type === PROPERTY_PANEL_TYPE.ADMIN_AUDIT_LOGS
                        ? propertyPanel.data?.requested?.payload
                        : undefined;

                const parseBase64 = (str) => {
                    // Base64의 패턴에 맞는지 확인 (A-Z, a-z, 0-9, +, /, =로 구성된지 확인)
                    const base64Pattern = /^(?:[A-Za-z0-9+\/]{4})*(?:[A-Za-z0-9+\/]{2}==|[A-Za-z0-9+\/]{3}=)?$/;

                    if (!str || !base64Pattern.test(str)) {
                        return undefined;
                    }

                    try {
                        const decodedStr = JSON.parse(decodeURIComponent(atob(str)));
                        return decodedStr;
                    } catch (e) {
                        // 디코딩 중 오류가 발생하면 Base64가 아님
                        return undefined;
                    }
                };

                return (
                    <Box p={2.5}>
                        <ReactJson
                            src={parseBase64(src) ?? src}
                            style={{ height: "100%" }}
                            iconStyle={"circle"}
                            collapsed={false}
                            name={false}
                            theme={"rjv-default"}
                        />
                    </Box>
                );
            }
            default:
                return <></>;
        }
    };

    const getPanelByErrorType = () => {
        if (propertyPanel.accessError === "NO_PERMISSION") {
            return (
                <PanelPermissionError
                    handleShareRequestModal={handleShareRequestModal}
                    panelType={propertyPanel.type}
                />
            );
        } else if (propertyPanel.accessError === "EXPIRED") {
            return <PanelExpiredError />;
        } else if (propertyPanel.error) {
            return <PanelCommonError />;
        }
        return null;
    };

    useEffect(() => {
        if (!propertyPanel.targetId || !propertyPanel.type) return;

        if (propertyPanel.type === PROPERTY_PANEL_TYPE.COLLECTION) setActiveTab(TAB_TYPE.DETAILS);
        if ([PROPERTY_PANEL_TYPE.CUSTOM_CONTENT, PROPERTY_PANEL_TYPE.ADMIN_AUDIT_LOGS].includes(propertyPanel.type)) {
            setActiveTab(TAB_TYPE.OVERVIEW);
        }
        if (propertyPanel.type === PROPERTY_PANEL_TYPE.PROMPT_PRESETS) {
            setActiveTab(TAB_TYPE.METADATA);
        }

        dispatch(getData({ id: propertyPanel.targetId, type: propertyPanel.type }));
    }, [propertyPanel.targetId, propertyPanel.type]);

    return (
        <VersionedComponent version={VERSION_CM_9766_COLLECTIONS}>
            <PanelHeader>
                <Grid container flexDirection={"row"}>
                    <Grid item flex={1} sx={{ overflow: "hidden", minWidth: 0 }}>
                        {propertyPanel.name ? (
                            <EditName
                                type={"panel"}
                                name={t(`common::label::${propertyPanel.name}`, propertyPanel.name)}
                            />
                        ) : (
                            <EditName
                                type={"panel"}
                                isEditable={isEditNameEditable}
                                isEdit={editNameActive}
                                name={propertyPanel.data?.name}
                                value={editName}
                                valueLength={editName?.length}
                                onChange={onHandleChangeEditName}
                                onEditClick={(e) => {
                                    setEditNameActive(true);
                                    setEditName(propertyPanel.data?.name);
                                }}
                                error={validationErrors.name?.level}
                                helperText={validationErrors.name?.message ?? null}
                                actions={
                                    <EditSubmitButtonGroup
                                        submitDisabled={
                                            editName === propertyPanel.data?.name ||
                                            validationErrors.name ||
                                            actionPending.editName
                                        }
                                        submitPending={actionPending.editName}
                                        onClickSubmit={onHandleClickEditName}
                                        onClickClose={() => {
                                            setEditNameActive(false);
                                            setValidationErrors({ ...validationErrors, name: null });
                                        }}
                                    />
                                }
                            />
                        )}
                    </Grid>
                    <Grid item>
                        <Tooltip title={t("common::label::Close")}>
                            <IconButton circled size={`medium`} onClick={onClickClose} sx={{ marginTop: 1 }}>
                                <IconLeave size={16} />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
            </PanelHeader>
            {propertyPanel.pending ? (
                <Loading></Loading>
            ) : (
                <React.Fragment>
                    {propertyPanel.accessError || propertyPanel.error ? (
                        getPanelByErrorType()
                    ) : (
                        <>
                            <PanelBody>
                                {propertyPanel.isShow && propertyPanel.data ? (
                                    <React.Fragment>
                                        {thumbnail}
                                        {tabOptions?.length > 1 && (
                                            <>
                                                <Box sx={{ marginLeft: `-1px` }}>
                                                    <Tabs
                                                        indicatorColor="secondary"
                                                        indicatorPosition={"top"}
                                                        isBordered
                                                        textColor={"secondary"}
                                                        value={activeTab}
                                                        onChangeTab={onHandleChangeTab}
                                                        items={tabOptions.map((el) => ({
                                                            ...el,
                                                            label: t(`common::label::${el.label}`, el.label),
                                                        }))}
                                                    />
                                                    {/*<Divider sx={{ marginTop: `-5px` }} />*/}
                                                </Box>
                                                <Divider sx={{ mt: `-1px` }} />
                                            </>
                                        )}
                                        <Grid item xs sx={{ overflow: `hidden auto`, flexGrow: 1 }} p={0.5}>
                                            {tabOptions.map((el) => (
                                                <TabPanel
                                                    key={el.value}
                                                    value={activeTab}
                                                    index={el.value}
                                                    sx={el.style}
                                                >
                                                    {getTabPanelContents(el.value)}
                                                </TabPanel>
                                            ))}
                                        </Grid>
                                    </React.Fragment>
                                ) : (
                                    <div>{t(`common::label::${"Not found"}`)}</div>
                                )}
                            </PanelBody>
                            {![PROPERTY_PANEL_TYPE.ADMIN_AUDIT_LOGS, PROPERTY_PANEL_TYPE.PROMPT_PRESETS].includes(
                                propertyPanel.type,
                            ) && (
                                <Box>
                                    <Button
                                        fullWidth
                                        size={"large"}
                                        variant={"contained"}
                                        color={"greySecondary"}
                                        sx={{
                                            height: `44px`,
                                            borderRadius: 0,
                                            justifyContent: `flex-start`,
                                            pl: 3,
                                            ".MuiButton-endIcon": {
                                                ml: 1.5,
                                            },
                                            "&:hover": {
                                                ".MuiButton-endIcon": {
                                                    ".MuiBox-root": {
                                                        left: `12px`,
                                                    },
                                                },
                                            },
                                        }}
                                        endIcon={
                                            <Box
                                                sx={{
                                                    position: `relative`,
                                                    left: `0`,
                                                    display: `inline-flex`,
                                                    alignItems: `center`,
                                                    justifyContent: `center`,
                                                    width: `22px`,
                                                    height: `22px`,
                                                    borderRadius: `50%`,
                                                    backgroundColor: theme.palette.primary.light,
                                                    transition: `all .2s linear`,
                                                }}
                                            >
                                                <IconForward size={12} />
                                            </Box>
                                        }
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            moveToDetails();
                                        }}
                                    >
                                        <Typography variant={"body1_400"}>Go to details</Typography>
                                    </Button>
                                </Box>
                            )}
                        </>
                    )}
                </React.Fragment>
            )}
        </VersionedComponent>
    );
};

export default PropertyPanelV2;
